
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FieldType, FilledBy, FormField, SelectOption } from "@/models";
import WysiwygEditor from "../WysiwygEditor";
import SelectOptionsEditor from "../SelectOptionsEditor";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { fieldTypeOptions, getFieldIcon } from "../utils/fieldIcons";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";

interface EditFieldDialogProps {
  field: FormField | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (
    label: string, 
    fieldType: FieldType, 
    filledBy: FilledBy, 
    isRequired: boolean, 
    content?: string, 
    options?: SelectOption[],
    name?: string,
    dataType?: string,
    mainApplicantOnly?: boolean
  ) => Promise<void>;
  isSubmitting: boolean;
}

const dataTypeOptions = [
  { value: 'generic', label: 'Generic' },
  { value: 'email', label: 'Email' },
  { value: 'address', label: 'Address' },
  { value: 'state', label: 'State' },
  { value: 'phone', label: 'Phone' },
  { value: 'country', label: 'Country' },
  { value: 'social_security', label: 'Social Security Number' },
  { value: 'date_of_birth', label: 'Date of Birth' },
];

const EditFieldDialog = ({ 
  field, 
  isOpen, 
  onClose,
  onUpdate,
  isSubmitting
}: EditFieldDialogProps) => {
  const { toast } = useToast();
  const [label, setLabel] = useState("");
  const [name, setName] = useState("");
  const [fieldType, setFieldType] = useState<FieldType>("text");
  const [originalFieldType, setOriginalFieldType] = useState<FieldType>("text");
  const [isRequired, setIsRequired] = useState(false);
  const [filledBy, setFilledBy] = useState<FilledBy>("requestor");
  const [content, setContent] = useState("");
  const [options, setOptions] = useState<SelectOption[]>([
    { label: "Option 1", value: "option_1" }
  ]);
  const [dataType, setDataType] = useState<string>("generic");
  const [fieldTypeChanged, setFieldTypeChanged] = useState(false);
  // Multi-applicant signatures (D1): only shown for signature/initials.
  const [mainApplicantOnly, setMainApplicantOnly] = useState(false);

  // A signature/initials field is signed by EVERY applicant unless flagged.
  const isSignatureField = fieldType === "signature" || fieldType === "initials";

  const generateNameFromLabel = (inputLabel: string): string => {
    return inputLabel
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')     
      .trim()                        
      .split(/\s+/)                  
      .slice(0, 6)                   
      .join('_');
  };

  const parseOptionsFromContent = (contentStr: string | undefined): SelectOption[] => {
    if (!contentStr) return [{ label: "Option 1", value: "option_1" }];
    
    try {
      const parsedOptions = JSON.parse(contentStr);
      console.log("Parsed options from content:", parsedOptions);
      
      if (Array.isArray(parsedOptions)) {
        return parsedOptions.map(opt => {
          if (typeof opt === 'object' && opt !== null) {
            return {
              label: opt.label || opt.text || "Option",
              value: opt.value || "option"
            };
          }
          return { label: String(opt), value: String(opt) };
        });
      }
    } catch (error) {
      console.error("Error parsing options from content:", error);
    }
    
    return [{ label: "Option 1", value: "option_1" }];
  };

  useEffect(() => {
    if (isOpen && field) {
      console.log("Loading field for edit:", field);
      setLabel(field.label || "");
      setName(field.name || "");
      setFieldType(field.field_type);
      setOriginalFieldType(field.field_type);
      setIsRequired(field.is_required);
      setMainApplicantOnly(field.main_applicant_only === true);
      setFilledBy(field.filled_by);
      setFieldTypeChanged(false);
      
      const fieldDataType = field.data_type || "generic";
      console.log("Setting data type to:", fieldDataType);
      setDataType(fieldDataType);
      
      if (field.field_type === "information") {
        setContent(field.content || "");
      } else if (field.field_type === "select") {
        if (field.options && field.options.length > 0) {
          console.log("Using field.options:", field.options);
          setOptions(field.options);
        } else if (field.content) {
          console.log("Parsing options from content:", field.content);
          const parsedOptions = parseOptionsFromContent(field.content);
          setOptions(parsedOptions);
        }
      }
    }
  }, [isOpen, field]);

  useEffect(() => {
    if (fieldType !== originalFieldType) {
      setFieldTypeChanged(true);
      
      // Reset content based on new field type
      if (fieldType === "information") {
        setContent("");
      } else if (fieldType === "select") {
        setOptions([{ label: "Option 1", value: "option_1" }]);
      }
    } else {
      setFieldTypeChanged(false);
    }
  }, [fieldType, originalFieldType]);
  
  const handleSubmit = async () => {
    console.log("handleSubmit called with form values:", { label, name, fieldType, isRequired, filledBy, dataType });
    
    if (!label.trim() && fieldType !== 'information') {
      toast({
        variant: "destructive",
        title: "Label is required",
        description: "Please enter a label for the field"
      });
      return;
    }
    
    if (fieldType === 'information' && !content.trim()) {
      toast({
        variant: "destructive",
        title: "Content is required",
        description: "Please enter content for the information field"
      });
      return;
    }
    
    if (fieldType === 'select' && (!options.length || !options[0].label.trim())) {
      toast({
        variant: "destructive",
        title: "Options are required",
        description: "Please add at least one option for the select field"
      });
      return;
    }
    
    try {
      console.log("Updating field...");
      
      let fieldContent;
      if (fieldType === 'information') {
        fieldContent = content;
      } else if (fieldType === 'select') {
        fieldContent = JSON.stringify(options);
      }
      
      await onUpdate(
        label.trim(), 
        fieldType,
        filledBy,
        isRequired,
        fieldContent,
        fieldType === 'select' ? options : undefined,
        name.trim() || generateNameFromLabel(label),
        dataType,
        isSignatureField ? mainApplicantOnly : false
      );
    } catch (error) {
      console.error("Error processing field:", error);
      toast({
        variant: "destructive",
        title: "Failed to update field",
        description: error instanceof Error ? error.message : "An unknown error occurred"
      });
    }
  };
  
  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!isSubmitting) {
          onClose();
        }
      }}
    >
      <DialogContent 
        className="sm:max-w-[600px] max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onPointerDownOutside={(e) => {
          if (e.target instanceof Element) {
            const target = e.target as Element;
            if (target.closest('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }
          if (isSubmitting) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Field</DialogTitle>
          <DialogDescription>
            Modify this field's properties
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-grow px-4">
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="field-type">Field Type</Label>
              <Select
                value={fieldType}
                onValueChange={(value) => {
                  console.log("Field type changed to", value);
                  setFieldType(value as FieldType);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger id="field-type">
                  <SelectValue placeholder="Select field type">
                    {fieldType && (
                      <div className="flex items-center space-x-2">
                        {getFieldIcon(fieldType)}
                        <span>{fieldType.charAt(0).toUpperCase() + fieldType.slice(1)}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {fieldTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center space-x-2">
                        {option.icon}
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldTypeChanged && (
                <p className="text-xs text-amber-500 font-medium">
                  Warning: Changing field type may cause data loss or unexpected behavior.
                </p>
              )}
            </div>
        
            {fieldType !== 'information' && (
              <div className="space-y-2">
                <Label htmlFor="field-label">Field Label</Label>
                <Input
                  id="field-label"
                  placeholder="Enter field label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            )}
          
            {fieldType !== 'information' && (
              <div className="space-y-2">
                <Label htmlFor="field-name">Field Name</Label>
                <Input
                  id="field-name"
                  placeholder="Enter field name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  System identifier for this field. Auto-generated from label if left blank.
                </p>
              </div>
            )}
        
            {fieldType === 'information' && (
              <div className="space-y-2">
                <Label htmlFor="field-content">Information Content</Label>
                <WysiwygEditor
                  value={content}
                  onChange={(newContent) => setContent(newContent)}
                  disabled={isSubmitting}
                />
              </div>
            )}
        
            {fieldType === 'select' && (
              <div className="space-y-2">
                <Label htmlFor="field-options">Options</Label>
                <SelectOptionsEditor
                  options={options}
                  onChange={(newOptions) => setOptions(newOptions)}
                  disabled={isSubmitting}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Data Type</Label>
              <Select
                value={dataType}
                onValueChange={(value) => {
                  console.log("Data type changed to:", value);
                  setDataType(value);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select data type" />
                </SelectTrigger>
                <SelectContent>
                  {dataTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Specifies the type of data this field will collect.
              </p>
            </div>
        
            {fieldType !== 'information' && (
              <div>
                <div className="space-y-2">
                  <Label htmlFor="filled-by">Filled By</Label>
                  <Select
                    value={filledBy}
                    onValueChange={(value) => setFilledBy(value as FilledBy)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="filled-by">
                      <SelectValue placeholder="Select who fills this field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="requestor">Requestor</SelectItem>
                      <SelectItem value="operations">Operations</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isSignatureField && (
                  <div className="rounded-md border border-border bg-muted/40 p-3 mt-2">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="main-applicant-only"
                        checked={mainApplicantOnly}
                        onCheckedChange={(checked) => setMainApplicantOnly(checked === true)}
                        disabled={isSubmitting}
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="main-applicant-only"
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          Only the main applicant
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {mainApplicantOnly
                            ? "Only the main applicant signs this field."
                            : "Every applicant on the order signs this field."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox 
                    id="is-required"
                    checked={isRequired} 
                    onCheckedChange={(checked) => setIsRequired(checked === true)}
                    disabled={isSubmitting}
                  />
                  <Label
                    htmlFor="is-required"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    onClick={() => {
                      if (!isSubmitting) {
                        setIsRequired(!isRequired);
                      }
                    }}
                  >
                    Required Field
                  </Label>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 border-t mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              console.log("Cancel button clicked");
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            disabled={isSubmitting}
            onClick={() => {
              console.log("Update Field button clicked");
              handleSubmit();
            }}
          >
            Update Field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditFieldDialog;
